import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import * as apiCalls from '@/api';
import { refreshAccessToken, setAccessToken, setSessionExpiredHandler } from '@/lib/api-client';
import type { PublicUser } from '@/types/api';

/**
 * Session state. This is the only global store in the app: everything else is
 * server state and lives in TanStack Query, which already caches, dedupes and
 * invalidates. Duplicating that into Pinia would just be two sources of truth.
 */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<PublicUser | null>(null);
  /** `true` until the initial refresh attempt settles, so guards can wait. */
  const isBootstrapping = ref(true);

  const isAuthenticated = computed(() => user.value !== null);
  const isAdmin = computed(() => user.value?.role === 'ADMIN');
  const isCustomer = computed(() => user.value?.role === 'CUSTOMER');

  const clear = (): void => {
    user.value = null;
    setAccessToken(null);
  };

  // The api client calls this when a refresh fails mid-session.
  setSessionExpiredHandler(clear);

  /**
   * Restores the session on a cold load. The access token only ever lives in
   * memory, so a page reload has nothing to read; the httpOnly refresh cookie is
   * what carries the session across reloads.
   */
  const bootstrap = async (): Promise<void> => {
    try {
      const token = await refreshAccessToken();
      if (!token) return;
      setAccessToken(token);
      const { user: current } = await apiCalls.fetchCurrentUser();
      user.value = current;
    } catch {
      clear();
    } finally {
      isBootstrapping.value = false;
    }
  };

  const signIn = async (whatsappNumber: string, password: string): Promise<PublicUser> => {
    const session = await apiCalls.login({ whatsappNumber, password });
    setAccessToken(session.accessToken);
    user.value = session.user;
    return session.user;
  };

  const signUp = async (input: {
    name: string;
    whatsappNumber: string;
    password: string;
    email: string;
    genres: string[];
  }): Promise<PublicUser> => {
    const session = await apiCalls.register(input);
    setAccessToken(session.accessToken);
    user.value = session.user;
    return session.user;
  };

  const signOut = async (): Promise<void> => {
    try {
      await apiCalls.logout();
    } finally {
      // Local state is cleared even if the network call fails: the user asked to
      // be signed out, and the refresh cookie expires on its own.
      clear();
    }
  };

  const applyUpdatedProfile = (next: PublicUser): void => {
    user.value = next;
  };

  return {
    user,
    isBootstrapping,
    isAuthenticated,
    isAdmin,
    isCustomer,
    bootstrap,
    signIn,
    signUp,
    signOut,
    applyUpdatedProfile,
  };
});
